import type {ReactElement} from 'react';
import React, {useEffect} from 'react';
import useDefaultPanelClose from '@Panels/hooks/useDefaultPanelClose';
import {Panel, PanelHeader} from '@Components/panel';
import {ContextualMenu} from '@Components/contextual-menu';
import {ContextualMenuToggleOption} from '@Components/contextual-menu/components/contextual-menu-toggle-option';
import {ContextualMenuTabToggleOption} from '@Components/contextual-menu/components/contextual-menu-tab-toggle-option';
import type {TabToggleData} from '@Components/tabs-toggle/tabs-toggle.types';
import {TabsToggleSize, TabToggleVariant} from '@Components/tabs-toggle/tabs-toggle.types';
import {BleedUnit, getDefaultValForUnit} from '@PosterWhiteboard/poster/bleed.class';
import {updateOptionsPanelHeight} from '@Components/poster-editor/library/poster-editor-library';
import {PageBleedUnitSizeOption} from '@Components/poster-editor/components/poster-editing-side-panel/components/page-bleed-unit-size-option';
import {useAppSelector} from '@/hooks';

interface PosterEditorBleedPanelProps {
  panelId: string;
}

const BLEED_PANEL_DEFAULT_HEIGHT = 114;
const BLEED_PANEL_ENABLED_HEIGHT = 252;

export default function PosterEditorBleedPanel({...props}: PosterEditorBleedPanelProps): ReactElement | null {
  const onClose = useDefaultPanelClose(props.panelId);
  const bleedUnits = useAppSelector((state) => {
    return state.posterEditor.posterObject?.bleed.unit;
  });
  const isBleedEnabled = useAppSelector((state) => {
    return state.posterEditor.posterObject?.bleed.enabled;
  });
  useEffect((): void => {
    let height = BLEED_PANEL_DEFAULT_HEIGHT;
    if (isBleedEnabled) {
      height = BLEED_PANEL_ENABLED_HEIGHT;
    }
    updateOptionsPanelHeight(props.panelId, `${height}px`);
  }, [isBleedEnabled]);

  if (isBleedEnabled === undefined || bleedUnits === undefined) {
    return null;
  }

  const getHeader = (): ReactElement => {
    return <PanelHeader onClose={onClose} title={window.i18next.t('pmwjs_bleed')} />;
  };

  const getBleedToggle = (): ReactElement => {
    return (
      <ContextualMenuToggleOption
        id="page-bleed-option"
        label={window.i18next.t('pmwjs_bleed')}
        isChecked={isBleedEnabled}
        onClick={(): void => {
          void window.posterEditor?.whiteboard?.updateFromObject(
            {
              bleed: {
                enabled: !isBleedEnabled,
              },
            },
            {
              undoable: false,
            }
          );
        }}
      />
    );
  };

  const getUnitData = (): TabToggleData[] => {
    return [
      {id: BleedUnit.INCHES, text: window.i18next.t('pmwjs_bleed_inch')},
      {id: BleedUnit.MILLI_METER, text: window.i18next.t('pmwjs_bleed_millimeter')},
    ];
  };

  const onChangeUnitType = (id: BleedUnit): void => {
    void window.posterEditor?.whiteboard?.updateFromObject(
      {
        bleed: {
          unit: id,
          val: getDefaultValForUnit(id),
        },
      },
      {
        undoable: false,
      }
    );
  };

  const getBleedSubOptions = (): ReactElement | null => {
    if (!isBleedEnabled) {
      return null;
    }
    return (
      <>
        <PageBleedUnitSizeOption />
        <ContextualMenuTabToggleOption
          id="bleed-dimension-unit-type"
          label={window.i18next.t('pmwjs_unit')}
          data={getUnitData()}
          selectedId={bleedUnits}
          onClick={onChangeUnitType}
          size={TabsToggleSize.MEDIUM}
          variant={TabToggleVariant.LIGHT}
        />
      </>
    );
  };

  const getContent = (): ReactElement => {
    return (
      <ContextualMenu contentClassName="spacing-m-b-0 spacing-p-t-1" id="bleed-mobile-options">
        {getBleedToggle()}
        {getBleedSubOptions()}
      </ContextualMenu>
    );
  };

  return <Panel panelHeader={getHeader()} panelContent={getContent()} />;
}
